import React, { useEffect, useMemo, useState, useRef, memo, createElement, Component } from 'react';
import { Mail, Save, Plus, Trash2, CheckCircle2, MapPin, Phone, Briefcase, FileText, Clock, Truck, Download, Eraser, X, Check, PenLine, FolderOpen, FilePlus2, Pencil, Archive, Upload, User, ArrowUpFromLine, ArrowDownToLine } from 'lucide-react';
import { listTickets, upsertTicket, deleteTicket, getLastSyncError, type SavedTicket } from '../utils/ticketStorage';
import { sendFormEmail } from '../utils/sendFormEmail';
interface MaterialRow {
  id: string;
  material: string;
  description: string;
  loads: number;
}
// Ticket numbers are SEQUENCE-based. A brand-new ticket is assigned the next
// number after the highest one already saved in the browser library — so once
// a ticket is saved, the next fresh ticket (on the next reload or via "New")
// advances by one. The sequence floor is 1207: if nothing (relevant) is saved
// yet, the first ticket is #1207. Because it keys off the max saved number, a
// manual overwrite carries forward too — save #1215 and the next new ticket
// starts at #1216.
const NEXT_TICKET_BASE = 1207;
// Pure: given the currently-known tickets, return the next number in sequence
// (highest saved + 1, floor 1207). Callers pass the in-memory list so this
// stays synchronous even though the tickets are fetched from the cloud.
function getNextTicketNumber(tickets: SavedTicket[]): string {
  let max = NEXT_TICKET_BASE - 1;
  for (const t of tickets) {
    const n = parseInt(String(t.ticketNumber).replace(/[^0-9]/g, ''), 10);
    if (!Number.isNaN(n) && n > max) max = n;
  }
  return String(max + 1);
}
// Format a 24-hour "HH:MM" value as a 12-hour "h:MM AM/PM" string for display
// (the stored value stays 24h so the totalHours math and cloud storage are
// unaffected). Returns '' for an empty/invalid value.
function formatTime12h(value: string): string {
  if (!value) return '';
  const [h, m] = value.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return value;
  const period = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}
// Round a HH:MM time string to the NEAREST 15 minutes (5:05 -> 5:00,
// 5:08 -> 5:15). Uses standard rounding, not always-up.
function roundTimeUp(value: string): string {
  if (!value) return value;
  const [h, m] = value.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return value;
  let totalMin = h * 60 + m;
  totalMin = Math.round(totalMin / 15) * 15;
  totalMin = totalMin % (24 * 60);
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
// Reusable drawing surface. Captures finger/stylus/mouse strokes and calls
// onStrokeEnd(dataUrl) after each stroke. `onDrawnChange` flags whether
// anything has been drawn yet so the parent can enable/disable its OK button.
// Bumping `clearSignal` wipes the canvas.
function SignatureCanvas({
  className,
  onStrokeEnd,
  onDrawnChange,
  clearSignal
}: {
  className: string;
  onStrokeEnd: (dataUrl: string) => void;
  onDrawnChange?: (hasDrawn: boolean) => void;
  clearSignal: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const hasDrawn = useRef(false);
  // Remember the backing-store size we last sized to, so a redundant resize
  // (e.g. a ResizeObserver firing with the same dimensions) doesn't needlessly
  // wipe the canvas mid-signature.
  const sizedTo = useRef<{
    w: number;
    h: number;
  } | null>(null);
  // Apply pen context settings (called after every (re)size, since resizing a
  // canvas resets its 2D context state).
  const applyPenStyle = (ctx: CanvasRenderingContext2D, ratio: number) => {
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1c1917';
  };
  // Size the backing store to the rendered size for crisp high-DPI lines.
  // CRITICAL for the mobile signing modal: that canvas is `absolute inset-0
  // w-full h-full` inside a container that is still laying out / animating when
  // the component first mounts, so a one-shot mount-time measurement could read
  // a 0x0 (or stale) rect. When that happened the backing store was empty,
  // getImageData() in exportTrimmed() read nothing, and the exported signature
  // PNG came back BLANK — which is exactly why signatures captured on the phone
  // saved as empty strings and showed missing on the PC. We now size robustly
  // (RAF + ResizeObserver below) and skip any resize that would produce a
  // zero-area canvas.
  const setup = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);
    if (w === 0 || h === 0) return;
    canvas.width = w * ratio;
    canvas.height = h * ratio;
    sizedTo.current = {
      w,
      h
    };
    const ctx = canvas.getContext('2d');
    if (ctx) applyPenStyle(ctx, ratio);
  };
  useEffect(() => {
    // Initial sizing across a couple of animation frames so the modal canvas is
    // measured only AFTER its container has finished laying out.
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      setup();
      raf2 = requestAnimationFrame(setup);
    });
    // Keep the backing store matched to the painted size. If the canvas grows
    // from a collapsed state (0 -> real size) this fires and sizes it before
    // the user can draw. We only resize when the CSS size actually changed so
    // an in-progress signature is never wiped by a no-op observer tick.
    const canvas = canvasRef.current;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => {
      const c = canvasRef.current;
      if (!c) return;
      const rect = c.getBoundingClientRect();
      const w = Math.round(rect.width);
      const h = Math.round(rect.height);
      if (w === 0 || h === 0) return;
      if (sizedTo.current && sizedTo.current.w === w && sizedTo.current.h === h) return;
      setup();
    }) : null;
    if (ro && canvas) ro.observe(canvas);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Clear when the parent bumps clearSignal.
  useEffect(() => {
    if (clearSignal === 0) return;
    setup();
    hasDrawn.current = false;
    onDrawnChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSignal]);
  const getPos = (e: React.PointerEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };
  const start = (e: React.PointerEvent) => {
    e.preventDefault();
    canvasRef.current?.setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = getPos(e);
  };
  const move = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || !last.current) return;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    last.current = pos;
    if (!hasDrawn.current) {
      hasDrawn.current = true;
      onDrawnChange?.(true);
    }
  };
  // Export the drawn signature CROPPED to its ink bounding box (with a little
  // padding). Trimming the huge empty canvas means the saved PNG is just the
  // signature, so it renders large and crisp in the small preview box instead
  // of appearing tiny surrounded by blank space.
  const exportTrimmed = (): string => {
    const canvas = canvasRef.current;
    if (!canvas) return '';
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas.toDataURL('image/png');
    const {
      width,
      height
    } = canvas;
    const {
      data
    } = ctx.getImageData(0, 0, width, height);
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    let found = false;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 10) {
          found = true;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (!found) return canvas.toDataURL('image/png');
    const pad = Math.round(8 * (window.devicePixelRatio || 1));
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(width, maxX + pad);
    maxY = Math.min(height, maxY + pad);
    const cropW = maxX - minX;
    const cropH = maxY - minY;
    const out = document.createElement('canvas');
    out.width = cropW;
    out.height = cropH;
    const outCtx = out.getContext('2d');
    if (!outCtx) return canvas.toDataURL('image/png');
    outCtx.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);
    return out.toDataURL('image/png');
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    if (hasDrawn.current && canvasRef.current) {
      onStrokeEnd(exportTrimmed());
    }
  };
  return <canvas ref={canvasRef} onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} onPointerCancel={end} className={className} style={{
    touchAction: 'none'
  }} data-id="element-2441" data-mp="2r3inc9tp4uq9" />;
}
// Signature pad. On desktop (fine pointer) it draws inline. On touch devices
// the inline box is a PREVIEW that stays inactive until tapped — tapping opens
// a large zoomed-in modal to sign with a finger, then "OK" commits the drawn
// signature (or "Cancel" discards it).
function SignaturePad({
  label,
  value,
  onChange
}: {
  label: string;
  value: string;
  onChange: (dataUrl: string) => void;
}) {
  // Coarse pointer => touch device => use the tap-to-zoom modal flow.
  const [isTouch, setIsTouch] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      setIsTouch(window.matchMedia('(pointer: coarse)').matches);
    }
  }, []);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftDrawn, setDraftDrawn] = useState(false);
  const [modalClear, setModalClear] = useState(0);
  const [inlineClear, setInlineClear] = useState(0);
  // Lock body scroll while the mobile signing modal is open.
  useEffect(() => {
    if (!modalOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [modalOpen]);
  const openModal = () => {
    setDraft(value);
    setDraftDrawn(false);
    setModalClear(c => c + 1);
    setModalOpen(true);
  };
  const confirmModal = () => {
    onChange(draft);
    setModalOpen(false);
  };
  const cancelModal = () => setModalOpen(false);
  const clearInline = () => {
    setInlineClear(c => c + 1);
    onChange('');
  };
  return <div data-id="element-2442" data-mp="3qc2k34azg3i1">
      <div className="text-sm text-stone-500 mb-2" data-id="element-2443" data-mp="3qcuguigbg79w">{label}</div>
      <div className="relative" data-id="element-2444" data-mp="3qe5j2du456yw">
        {isTouch ?
      // TOUCH: inactive preview box. Shows the captured signature if one
      // exists, otherwise a "Tap to sign" prompt. Tapping opens the modal.
      <button type="button" onClick={openModal} className="w-full h-28 bg-transparent border border-stone-200 hover:border-stone-300 transition-colors rounded-md flex items-center justify-center overflow-hidden p-2 print:h-24" aria-label={`${label} — tap to sign`} data-id="element-2445" data-mp="32mqa427tzreo">
            {value ? <img src={value} alt={label} className="max-h-full max-w-full object-contain" data-id="element-2446" data-mp="18dbg9m3p12ny" /> : <span className="pdf-hide flex items-center gap-1.5 text-xs font-bold text-stone-400 print:hidden" data-id="element-2447" data-mp="yhhvxgjqg4vm">
                <PenLine className="w-4 h-4" data-id="element-2448" data-mp="1w35sc1g6un75" /> Tap to sign
              </span>}
          </button> :
      // DESKTOP: draw inline with the mouse.
      <>
            <SignatureCanvas className="w-full h-24 bg-transparent border border-stone-200 hover:border-stone-300 transition-colors rounded-md cursor-crosshair print:h-24" onStrokeEnd={onChange} clearSignal={inlineClear} data-id="element-2449" data-mp="1m59idcyzlyam" />
            {!value && <span className="pdf-hide pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-stone-400 print:hidden" data-id="element-2450" data-mp="6ohia6v2lhca">
                Sign here
              </span>}
          </>}
        {/* Clear control — hidden in PDF/print. For touch it clears the saved
            signature; for desktop it wipes the inline canvas. */}
        <button type="button" onClick={isTouch ? () => onChange('') : clearInline} className="pdf-hide absolute top-1.5 right-1.5 flex items-center gap-1 text-[10px] font-bold text-stone-400 hover:text-red-500 transition-colors print:hidden" data-id="element-2451" data-mp="2w4u594wmi7fr">
          <Eraser className="w-3 h-3" data-id="element-2452" data-mp="3a422vtd517hu" /> Clear
        </button>
      </div>

      {/* Mobile zoomed signing modal */}
      {modalOpen && <div className="pdf-hide fixed inset-0 z-[9999] flex flex-col bg-stone-900/70 backdrop-blur-sm print:hidden" role="dialog" aria-modal="true" aria-label={`${label} signature`} data-id="element-2453" data-mp="3f23ksaytv0rq">
          <div className="flex-1 flex flex-col p-4 pt-6" data-id="element-2454" data-mp="1kr85kpt50nfz">
            <div className="flex items-center justify-between mb-3" data-id="element-2455" data-mp="pdxl7sxik5pj">
              <span className="text-sm font-bold uppercase tracking-wider text-white" data-id="element-2456" data-mp="kg4d2ucnh1mm">
                {label}
              </span>
              <button type="button" onClick={cancelModal} className="p-2 rounded-full bg-white/10 text-white active:bg-white/20" aria-label="Cancel" data-id="element-2457" data-mp="zc3h9twn2hmh">
                <X className="w-5 h-5" data-id="element-2458" data-mp="6or0k0d5t413" />
              </button>
            </div>
            <div className="relative flex-1 bg-white rounded-2xl overflow-hidden shadow-2xl" data-id="element-2459" data-mp="1qxsf1sapzy6">
              <SignatureCanvas className="absolute inset-0 w-full h-full cursor-crosshair" onStrokeEnd={setDraft} onDrawnChange={setDraftDrawn} clearSignal={modalClear} data-id="element-2460" data-mp="gf2coa6zyjj2" />
              {!draftDrawn && <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-base text-stone-300 font-medium" data-id="element-2461" data-mp="3f37wo9pwznrs">
                  Sign here with your finger
                </span>}
              {/* Baseline signing line */}
              <div className="pointer-events-none absolute left-6 right-6 bottom-14 border-b-2 border-dashed border-stone-200" data-id="element-2462" data-mp="1koc8hvsegmm9" />
            </div>
            <div className="flex items-center gap-3 mt-4 pb-2" data-id="element-2463" data-mp="1pnx90an7x313">
              <button type="button" onClick={() => {
            setModalClear(c => c + 1);
            setDraft('');
          }} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 text-white font-bold text-sm active:bg-white/20" data-id="element-2464" data-mp="2dc83mw2bm6gx">
                <Eraser className="w-4 h-4" data-id="element-2465" data-mp="28btsh55fageo" /> Clear
              </button>
              <button type="button" onClick={confirmModal} disabled={!draftDrawn} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brand-orange text-white font-bold text-base active:bg-brand-orangeHover disabled:opacity-40" data-id="element-2466" data-mp="svtl923pxj2x">
                <Check className="w-5 h-5" data-id="element-2467" data-mp="2uo57hdec3x1j" /> OK
              </button>
            </div>
          </div>
        </div>}
    </div>;
}
// Time field that ALWAYS displays as 12-hour AM/PM regardless of the device's
// locale (a native <input type="time"> renders 24h on devices set to 24h, which
// is why the form showed "14:30"). We render the AM/PM text ourselves and put a
// transparent native time input on top so tapping still opens the OS time
// picker. The native input keeps the canonical 24h "HH:MM" value that the
// totalHours math and cloud storage rely on. Because the visible value is real
// text (not an <input>), it also survives PDF export unchanged as AM/PM.
function TimeField({
  value,
  onChange,
  ariaLabel
}: {
  value: string;
  onChange: (val: string) => void;
  ariaLabel: string;
}) {
  return <div className="relative w-full" data-id="element-2468" data-mp="8nads1jmpp2n">
      <span className={`block w-full text-lg text-center whitespace-nowrap py-0 ${value ? 'text-stone-900' : 'text-stone-400'}`} data-id="element-2469" data-mp="8m91xuusgyjv">
        {value ? formatTime12h(value) : '—:—'}
      </span>
      {/* Transparent native picker overlay — opens the OS time selector on tap
                                      while the AM/PM text above stays visible. Hidden from PDF/print. */}
      <input type="time" value={value} onChange={e => onChange(roundTimeUp(e.target.value))} aria-label={ariaLabel} className="pdf-hide absolute inset-0 w-full h-full opacity-0 cursor-pointer print:hidden" data-id="element-2470" data-mp="3lulvn5jwb357" />
    </div>;
}
const MinimalInput = ({
  value,
  onChange,
  placeholder,
  type = 'text',
  className = '',
  icon
}: {
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  className?: string;
  // Optional small leading icon rendered in front of the field. Stays visible
  // in the PDF export (the icon is a plain SVG, not an input).
  icon?: React.ReactNode;
}) => <div className="flex items-center gap-2" data-id="element-2471" data-mp="1w6l4m4f6yyb7">
    {icon && <span className="shrink-0 text-[#00899B]" data-id="element-2472" data-mp="29wydc8zwqh1l">{icon}</span>}
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} className={`w-full bg-transparent border-b border-transparent hover:border-stone-200 focus:border-stone-300 focus:ring-0 px-0 py-1 text-sm text-stone-800 transition-colors ${className}`} data-id="element-2473" data-mp="29yj7vym81zzg" />
  </div>;
// Choose the leading icon for a Job Description field based on its text. An
// "Export" job shows an outbound arrow, an "Import" job an inbound arrow, and
// anything else falls back to a generic document icon.
function descriptionIcon(text: string): React.ReactNode {
  const t = (text || '').toLowerCase();
  if (t.includes('export')) return <ArrowUpFromLine className="w-4 h-4" data-id="element-2474" data-mp="tiyivdjtzjlq" />;
  if (t.includes('import')) return <ArrowDownToLine className="w-4 h-4" data-id="element-2475" data-mp="tl2vqkzpjsy5" />;
  return <FileText className="w-4 h-4" data-id="element-2476" data-mp="3573a8n5u6os3" />;
}
// Today's date (YYYY-MM-DD) in Los Angeles / Pacific time, regardless of the
// device's own timezone. 'en-CA' formats as ISO YYYY-MM-DD, and the
// America/Los_Angeles zone keeps PST/PDT correct year-round (auto DST).
function todayInLA(): string {
  return new Date().toLocaleDateString('en-CA', {
    timeZone: 'America/Los_Angeles'
  });
}
export function DailyTicketForm() {
  const [date, setDate] = useState(todayInLA());
  // Ticket # is sequence-based: on mount it seeds to the next number after the
  // highest saved ticket (floor 1207). The user can still type over it; a
  // manual value is carried into the sequence when saved. `ticketOverridden`
  // simply flags that the operator has taken manual control this session.
  // Seeds to the floor; the mount loader replaces it with the real next number
  // once tickets are fetched from the cloud (unless the user overrides it).
  const [ticketNumber, setTicketNumber] = useState(String(NEXT_TICKET_BASE));
  const ticketOverridden = useRef(false);
  const [jobName, setJobName] = useState('');
  const [customer, setCustomer] = useState('');
  const [jobAddress, setJobAddress] = useState('');
  const [projectPhoneEmail, setProjectPhoneEmail] = useState('');
  const [billToName, setBillToName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [timeIn, setTimeIn] = useState('');
  const [timeOut, setTimeOut] = useState('');
  const [materials, setMaterials] = useState<MaterialRow[]>([{
    id: '1',
    material: '',
    description: '',
    loads: 0
  }, {
    id: '2',
    material: '',
    description: '',
    loads: 0
  }]);
  const [notes, setNotes] = useState('');
  const [driverSignature, setDriverSignature] = useState('');
  const [companyRepSignature, setCompanyRepSignature] = useState('');
  const [isEmailing, setIsEmailing] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  // Saved-tickets "library" (Supabase cloud). currentId tracks which saved
  // record (if any) is loaded so Save updates it in place instead of copying.
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [savedTickets, setSavedTickets] = useState<SavedTicket[]>([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const restoreInputRef = useRef<HTMLInputElement>(null);
  // On mount, pull every ticket from the cloud. Seed a fresh form's ticket #
  // to the next sequence number once the list arrives.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLibraryLoading(true);
      const {
        tickets,
        fromCache
      } = await listTickets();
      if (cancelled) return;
      setSavedTickets(tickets);
      setIsOffline(fromCache);
      if (!ticketOverridden.current) {
        setTicketNumber(getNextTicketNumber(tickets));
      }
      setLibraryLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  // Total hours rounded UP to the nearest 15 min (0.25 / 0.50 / 0.75).
  const totalHours = useMemo(() => {
    if (!timeIn || !timeOut) return '';
    const [inH, inM] = timeIn.split(':').map(Number);
    const [outH, outM] = timeOut.split(':').map(Number);
    let diffMin = outH * 60 + outM - (inH * 60 + inM);
    if (diffMin < 0) diffMin += 24 * 60;
    const roundedMin = Math.ceil(diffMin / 15) * 15;
    const hours = Math.floor(roundedMin / 60);
    const fraction = roundedMin % 60 / 60;
    return (hours + fraction).toFixed(2);
  }, [timeIn, timeOut]);
  const totalLoads = useMemo(() => {
    return materials.reduce((sum, row) => sum + (Number(row.loads) || 0), 0);
  }, [materials]);
  const handleAddRow = () => {
    setMaterials([...materials, {
      id: Math.random().toString(),
      material: '',
      description: '',
      loads: 0
    }]);
  };
  const handleRemoveRow = (id: string) => {
    setMaterials(materials.filter(m => m.id !== id));
  };
  const handleUpdateRow = (id: string, field: keyof MaterialRow, value: string | number) => {
    setMaterials(materials.map(m => m.id === id ? {
      ...m,
      [field]: value
    } : m));
  };
  // Known Bill-To customers with saved contact details. When the entered name
  // matches (case-insensitive, whitespace-tolerant), email + phone auto-fill.
  const BILL_TO_CONTACTS: Record<string, {
    email: string;
    phone: string;
  }> = {
    'pierce trucking': {
      email: 'piercetruckinginc@hotmail.com',
      phone: '(425) 308-8723'
    }
  };
  const handleBillToName = (value: string) => {
    setBillToName(value);
    const key = value.trim().toLowerCase().replace(/\s+/g, ' ');
    const match = BILL_TO_CONTACTS[key];
    if (match) {
      setEmail(match.email);
      setPhone(match.phone);
    }
  };
  // Known Project-info customers with saved phone/email. When the entered
  // customer matches (case-insensitive, whitespace-tolerant), the Project
  // Phone / Email box auto-fills.
  const PROJECT_CONTACTS: Record<string, string> = {
    'deeny construction': '(206) 722-0633'
  };
  const handleCustomer = (value: string) => {
    setCustomer(value);
    const key = value.trim().toLowerCase().replace(/\s+/g, ' ');
    const match = PROJECT_CONTACTS[key];
    if (match) {
      setProjectPhoneEmail(match);
    }
  };
  const loadScript = (src: string) => new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(s);
  });
  // Build the ticket PDF and return the jsPDF instance + a filename. Shared by
  // both "Download PDF" and "Email Ticket" so the two always produce the SAME
  // document. Toggles `isDownloading` (which applies the `.exporting` class to
  // hide interactive controls + force the desktop layout during capture).
  const buildTicketPdf = async (): Promise<{
    pdf: any;
    blob: Blob;
    fileName: string;
  } | null> => {
    const element = document.getElementById('daily-ticket-content');
    if (!element) return null;
    setIsDownloading(true);
    // Wait two animation frames so React commits the `.exporting` class and the
    // browser paints the hidden/desktop state before we snapshot it.
    await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    try {
      if (!(window as any).html2canvas) {
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
      }
      if (!(window as any).jspdf) {
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      }
      const html2canvas = (window as any).html2canvas;
      const {
        jsPDF
      } = (window as any).jspdf;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: 1024,
        onclone: (doc: Document) => {
          doc.querySelectorAll('.pdf-hide').forEach(el => (el as HTMLElement).style.display = 'none');
          const root = doc.getElementById('daily-ticket-content');
          if (root) {
            root.classList.add('exporting');
            root.style.width = '896px';
            root.style.maxWidth = '896px';
          }
          if (root) {
            root.querySelectorAll('input, textarea').forEach(el => {
              const field = el as HTMLInputElement | HTMLTextAreaElement;
              const div = doc.createElement('div');
              div.textContent = field.value || '\u00A0';
              div.className = field.className;
              div.style.whiteSpace = 'pre-wrap';
              div.style.wordBreak = 'break-word';
              div.style.height = 'auto';
              div.style.minHeight = '1.25rem';
              div.style.lineHeight = '1.5';
              field.parentNode?.replaceChild(div, field);
            });
          }
        }
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.98);
      const pdf = new jsPDF({
        unit: 'in',
        format: 'letter',
        orientation: 'portrait'
      });
      const pageW = 8.5;
      const pageH = 11;
      const margin = 0.4;
      const availW = pageW - margin * 2;
      const availH = pageH - margin * 2;
      const ratio = canvas.width / canvas.height;
      let imgW = availW;
      let imgH = imgW / ratio;
      if (imgH > availH) {
        imgH = availH;
        imgW = imgH * ratio;
      }
      const x = (pageW - imgW) / 2;
      const y = margin;
      pdf.addImage(imgData, 'JPEG', x, y, imgW, imgH);
      const fileName = `DailyTicket_${ticketNumber || 'Draft'}_${date}.pdf`;
      const blob: Blob = pdf.output('blob');
      return {
        pdf,
        blob,
        fileName
      };
    } finally {
      setIsDownloading(false);
    }
  };
  const handleDownloadPdf = async () => {
    try {
      const result = await buildTicketPdf();
      if (result) result.pdf.save(result.fileName);
    } catch (err) {
      console.error('Failed to generate PDF', err);
      alert('Failed to generate PDF. Please try again.');
    }
  };
  // Gather every editable field into a plain object for persistence. Derived
  // values (totalHours / totalLoads) are intentionally NOT stored — they're
  // recomputed from timeIn/timeOut and materials whenever a ticket is loaded.
  const collectFields = () => ({
    ticketNumber,
    date,
    jobName,
    customer,
    jobAddress,
    projectPhoneEmail,
    billToName,
    email,
    phone,
    timeIn,
    timeOut,
    materials,
    notes,
    driverSignature,
    companyRepSignature
  });
  // Save (or update) the current ticket into the browser library. If a saved
  // record is already loaded (currentId set) it's updated in place; otherwise a
  // new record is created and becomes the current one.
  const handleSaveToLibrary = async () => {
    setIsSaving(true);
    try {
      const record = await upsertTicket({
        id: currentId ?? undefined,
        ...collectFields()
      });
      setCurrentId(record.id);
      const {
        tickets,
        fromCache
      } = await listTickets();
      setSavedTickets(tickets);
      setIsOffline(fromCache);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (err) {
      console.error(err);
      alert('Could not save to the cloud. Check your connection and try again. If this keeps happening, the database table may not be set up yet.');
    } finally {
      setIsSaving(false);
    }
  };
  // Load a saved ticket back into every field for viewing / editing.
  const handleLoadTicket = (t: SavedTicket) => {
    ticketOverridden.current = true; // keep the loaded ticket's own number
    setTicketNumber(t.ticketNumber);
    setDate(t.date);
    setJobName(t.jobName);
    setCustomer(t.customer);
    setJobAddress(t.jobAddress);
    setProjectPhoneEmail(t.projectPhoneEmail);
    setBillToName(t.billToName);
    setEmail(t.email);
    setPhone(t.phone);
    setTimeIn(t.timeIn);
    setTimeOut(t.timeOut);
    setMaterials(t.materials && t.materials.length ? t.materials : [{
      id: '1',
      material: '',
      description: '',
      loads: 0
    }, {
      id: '2',
      material: '',
      description: '',
      loads: 0
    }]);
    setNotes(t.notes);
    setDriverSignature(t.driverSignature);
    setCompanyRepSignature(t.companyRepSignature);
    setCurrentId(t.id);
    setLibraryOpen(false);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  // Start a fresh, blank ticket. The ticket # advances to the next number in
  // the sequence (highest saved + 1, floor 1207).
  const handleNewTicket = () => {
    const today = todayInLA();
    ticketOverridden.current = false;
    setDate(today);
    setTicketNumber(getNextTicketNumber(savedTickets));
    setJobName('');
    setCustomer('');
    setJobAddress('');
    setProjectPhoneEmail('');
    setBillToName('');
    setEmail('');
    setPhone('');
    setTimeIn('');
    setTimeOut('');
    setMaterials([{
      id: '1',
      material: '',
      description: '',
      loads: 0
    }, {
      id: '2',
      material: '',
      description: '',
      loads: 0
    }]);
    setNotes('');
    setDriverSignature('');
    setCompanyRepSignature('');
    setCurrentId(null);
    setLibraryOpen(false);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleDeleteTicket = async (id: string) => {
    // Optimistically drop it from the list, then confirm against the cloud.
    setSavedTickets(prev => prev.filter(t => t.id !== id));
    if (id === currentId) setCurrentId(null);
    try {
      await deleteTicket(id);
      const {
        tickets,
        fromCache
      } = await listTickets();
      setSavedTickets(tickets);
      setIsOffline(fromCache);
    } catch (err) {
      console.error(err);
      alert('Could not delete from the cloud. Check your connection.');
      const {
        tickets
      } = await listTickets();
      setSavedTickets(tickets);
    }
  };
  // Backup: download EVERY saved ticket as a single JSON file. Pulls the fresh
  // list from the cloud so the backup is complete even if the drawer is stale.
  const handleBackupAll = async () => {
    setIsBackingUp(true);
    try {
      const {
        tickets
      } = await listTickets();
      const backup = {
        app: 'BlackRock Daily Ticket',
        exportedAt: new Date().toISOString(),
        count: tickets.length,
        tickets
      };
      const blob = new Blob([JSON.stringify(backup, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BlackRock_Tickets_Backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Could not build the backup. Please try again.');
    } finally {
      setIsBackingUp(false);
    }
  };
  // Restore: read a backup JSON file and upsert every ticket back into the
  // cloud. Existing tickets with the same id are updated; new ones are added.
  const handleRestoreFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setIsRestoring(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      // Accept either a raw array of tickets or the wrapped backup shape.
      const incoming: SavedTicket[] = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.tickets) ? parsed.tickets : [];
      if (!incoming.length) {
        alert('That file has no tickets to restore.');
        return;
      }
      let restored = 0;
      for (const t of incoming) {
        if (!t || typeof t !== 'object') continue;
        // Re-upsert keeping each ticket's original id so it merges rather than
        // duplicating. collectFields-style shape is passed straight through.
        const {
          id,
          updatedAt,
          ...rest
        } = t as SavedTicket;
        await upsertTicket({
          id,
          ...(rest as Omit<SavedTicket, 'id' | 'updatedAt'>)
        });
        restored++;
      }
      const {
        tickets,
        fromCache
      } = await listTickets();
      setSavedTickets(tickets);
      setIsOffline(fromCache);
      alert(`Restored ${restored} ticket${restored === 1 ? '' : 's'}.`);
    } catch (err) {
      console.error(err);
      alert('Could not read that backup file. Make sure it is a valid backup JSON.');
    } finally {
      setIsRestoring(false);
    }
  };
  const handleSaveLocally = () => {
    const data = {
      ticketNumber,
      date,
      jobName,
      customer,
      jobAddress,
      projectPhoneEmail,
      billToName,
      email,
      phone,
      timeIn,
      timeOut,
      totalHours,
      materials,
      totalLoads,
      notes,
      driverSignature,
      companyRepSignature
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `DailyTicket_${ticketNumber || 'Draft'}_${date}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  // Send the ticket directly to dispatch through the same EmailJS delivery
  // route used by the live ordering flow. This is intentionally independent of
  // the device mail/share sheet: a desktop user should not need a local mail
  // app, and the Bill To email must never be mistaken for the dispatch address.
  const handleEmail = async () => {
    setIsEmailing(true);
    setEmailSuccess(false);
    const subject = `Daily Ticket #${ticketNumber}${customer ? ` — ${customer}` : ''}`;
    const materialsText = materials.filter(m => m.material || m.description || m.loads > 0).map(m => `• ${m.material || 'Material not specified'} — ${m.description || 'No job description'} — ${m.loads || 0} load${m.loads === 1 ? '' : 's'}`).join('\n');
    const message = [subject, `Date: ${date || 'Not specified'}`, customer ? `Customer: ${customer}` : '', jobName ? `Job / Address: ${jobName}` : '', projectPhoneEmail ? `Project Contact: ${projectPhoneEmail}` : '', billToName ? `Bill To: ${billToName}` : '', phone ? `Bill To Phone: ${phone}` : '', '', 'MATERIALS', materialsText || 'No material lines entered', '', `Total Loads: ${totalLoads}`, `Time In: ${timeIn ? formatTime12h(timeIn) : 'Not entered'}`, `Time Out: ${timeOut ? formatTime12h(timeOut) : 'Not entered'}`, `Total Hours: ${totalHours || 'Not entered'}`, notes ? `\nNOTES\n${notes}` : ''].filter(Boolean).join('\n');
    try {
      const sent = await sendFormEmail({
        subject,
        to_name: 'Dispatch',
        from_name: 'BlackRock Groundworks Daily Ticket',
        email: email || undefined,
        _replyto: email || undefined,
        'Ticket Number': ticketNumber,
        Date: date,
        Customer: customer,
        'Job / Address': jobName,
        'Project Contact': projectPhoneEmail,
        'Bill To': billToName,
        'Bill To Phone': phone,
        'Total Loads': totalLoads,
        'Total Hours': totalHours || 'Not entered',
        message
      });
      if (!sent) {
        throw new Error('Dispatch email was not accepted');
      }
      setEmailSuccess(true);
      setTimeout(() => setEmailSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to send ticket to dispatch', err);
      alert('Could not send this ticket to dispatch. Please try again or call (206) 619-3742.');
    } finally {
      setIsEmailing(false);
    }
  };
  return <div id="daily-ticket-page" style={{
    colorScheme: 'light'
  }} className="min-h-screen bg-[#f5f5f4] py-8 px-4 sm:px-6 lg:px-8 print:bg-white print:py-0 print:px-0 print:min-h-0" data-id="element-2477" data-mp="36xf09vptmg04">
      {/* Action Bar (Hidden in Print / PDF) */}
      <div id="action-buttons" className="pdf-hide max-w-4xl mx-auto mb-6 flex flex-wrap items-center justify-end gap-3 print:hidden" data-id="element-2478" data-mp="b1ifvjl6yxwj">
        {currentId && <span className="mr-auto flex items-center gap-1.5 text-xs font-bold text-[#6a867b] bg-[#8aa69b]/10 border border-[#8aa69b]/30 rounded-full px-3 py-1.5" data-id="element-2479" data-mp="kyde01djywaa">
            <Pencil className="w-3.5 h-3.5" data-id="element-2480" data-mp="pr6luy4bikql" /> Editing saved ticket
          </span>}
        <button onClick={handleNewTicket} className="flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-lg text-sm font-bold text-stone-700 hover:bg-stone-50 transition-colors shadow-sm" data-id="element-2481" data-mp="uqswczsrhmvp">
          <FilePlus2 className="w-4 h-4" data-id="element-2482" data-mp="18ohh87mtwu0r" /> New
        </button>
        <button onClick={() => setLibraryOpen(true)} className="relative flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-lg text-sm font-bold text-stone-700 hover:bg-stone-50 transition-colors shadow-sm" data-id="element-2483" data-mp="18lvaga7riijl">
          <FolderOpen className="w-4 h-4" data-id="element-2484" data-mp="2aacobmd7j6aa" /> Saved Tickets
          {savedTickets.length > 0 && <span className="ml-0.5 min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-[#8aa69b] text-white text-[11px] font-bold" data-id="element-2485" data-mp="2f8e683ywczk6">
              {savedTickets.length}
            </span>}
        </button>
        <button onClick={handleSaveToLibrary} disabled={isSaving} className="flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-lg text-sm font-bold text-stone-700 hover:bg-stone-50 transition-colors shadow-sm disabled:opacity-50" data-id="element-2486" data-mp="1rkd5hh7ymnfb">
          {isSaving ? <CheckCircle2 className="w-4 h-4 animate-pulse" data-id="element-2487" data-mp="25cb2wusb6uvn" /> : savedFlash ? <CheckCircle2 className="w-4 h-4 text-[#8aa69b]" data-id="element-2488" data-mp="18uewmywc8qam" /> : <Save className="w-4 h-4" data-id="element-2489" data-mp="1cvgy7mdq9efr" />}
          {isSaving ? 'Saving...' : savedFlash ? 'Saved!' : currentId ? 'Update' : 'Save'}
        </button>
        <button onClick={handleSaveLocally} className="flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-lg text-sm font-bold text-stone-700 hover:bg-stone-50 transition-colors shadow-sm" data-id="element-2490" data-mp="10dxs3e3um72z">
          <Download className="w-4 h-4" data-id="element-2491" data-mp="reasmxuojbck" /> Export JSON
        </button>
        <button onClick={handleEmail} disabled={isEmailing} className="flex items-center gap-2 px-4 py-2 bg-[#8aa69b]/10 border border-[#8aa69b]/30 rounded-lg text-sm font-bold text-[#8aa69b] hover:bg-[#8aa69b]/20 transition-colors shadow-sm disabled:opacity-50" data-id="element-2492" data-mp="mdvbdv7a1jpb">
          {emailSuccess ? <CheckCircle2 className="w-4 h-4" data-id="element-2493" data-mp="8jvqx0gzh4l1" /> : <Mail className="w-4 h-4" data-id="element-2494" data-mp="3larh39k2oxn" />}
          {isEmailing ? 'Sending...' : emailSuccess ? 'Sent to Dispatch!' : 'Send to Dispatch'}
        </button>
        <button onClick={handleDownloadPdf} disabled={isDownloading} className="flex items-center gap-2 px-5 py-2 bg-[#8aa69b] text-white rounded-lg text-sm font-bold hover:bg-[#7a968b] transition-colors shadow-sm disabled:opacity-50" data-id="element-2495" data-mp="3l3zsm2hiyqj6">
          {isDownloading ? <CheckCircle2 className="w-4 h-4 animate-pulse" data-id="element-2496" data-mp="3g8aormyh2nh2" /> : <Download className="w-4 h-4" data-id="element-2497" data-mp="7qwpsgv3pjii" />}
          {isDownloading ? 'Generating...' : 'Download PDF'}
        </button>
      </div>

      {/* Saved Tickets library — a right-side drawer listing every ticket saved
                                                                in this browser. Reopen loads it back into the form for editing;
                                                                delete removes it. Never appears in print / PDF. */}
      {libraryOpen && <div className="pdf-hide fixed inset-0 z-[9998] flex justify-end print:hidden" role="dialog" aria-modal="true" aria-label="Saved tickets" data-id="element-2498" data-mp="vhjyv5gr6f6n">
          <div className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm" onClick={() => setLibraryOpen(false)} data-id="element-2499" data-mp="2b6i2rejglas9" />
          <div className="relative w-full max-w-md h-full bg-white shadow-2xl flex flex-col" data-id="element-2500" data-mp="2kxcswtztsuiz">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200" data-id="element-2501" data-mp="1x7ijkmepto3h">
              <div data-id="element-2502" data-mp="1s8fdqzqwvdug">
                <h2 className="text-lg font-bold text-stone-900" data-id="element-2503" data-mp="1s98la2xp27dn">
                  Saved Tickets
                </h2>
                <p className="text-xs text-stone-500" data-id="element-2504" data-mp="268zu7p9csxks">
                  {isOffline ? 'Cloud unreachable — showing cached copies' : 'Synced to the cloud'}{' '}
                  · {savedTickets.length} saved
                </p>
                {/* Surface the actual failure reason. A silent "offline" label
                    made a paused/misconfigured Supabase project look identical
                    to a genuine network drop. */}
                {isOffline && getLastSyncError() && <p className="mt-1 text-[11px] leading-snug text-red-600 break-words" data-id="element-2505" data-mp="gu8o0dp40fdx">
                    {getLastSyncError()}
                  </p>}
              </div>
              <button type="button" onClick={() => setLibraryOpen(false)} className="p-2 rounded-full text-stone-500 hover:bg-stone-100 transition-colors" aria-label="Close" data-id="element-2506" data-mp="qrn42c74f25p">
                <X className="w-5 h-5" data-id="element-2507" data-mp="14hgq6mr8r8bq" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-id="element-2508" data-mp="19gjycg2ipunx">
              {libraryLoading ? <div className="flex flex-col items-center justify-center text-center h-full text-stone-400 px-6" data-id="element-2509" data-mp="1eg67adbbcoqx">
                  <span className="h-8 w-8 rounded-full border-2 border-[#8aa69b] border-t-transparent animate-spin mb-3" data-id="element-2510" data-mp="1je7unafrva87" />
                  <p className="font-bold text-stone-500" data-id="element-2511" data-mp="33ymp461hx1k7">Loading tickets…</p>
                </div> : savedTickets.length === 0 ? <div className="flex flex-col items-center justify-center text-center h-full text-stone-400 px-6" data-id="element-2512" data-mp="2tyvqbs5wk0eb">
                  <FolderOpen className="w-10 h-10 mb-3" data-id="element-2513" data-mp="2p0aqvuyh5kqx" />
                  <p className="font-bold text-stone-500" data-id="element-2514" data-mp="2p1voxujzzltj">
                    No saved tickets yet
                  </p>
                  <p className="text-sm mt-1" data-id="element-2515" data-mp="3hoggow4dxzxm">
                    Fill out a ticket and press{' '}
                    <span className="font-bold" data-id="element-2516" data-mp="3cxghfk7sn5ry">Save</span> to keep it here for
                    editing later.
                  </p>
                </div> : savedTickets.map(t => <div key={t.id} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${t.id === currentId ? 'border-[#8aa69b] bg-[#8aa69b]/5' : 'border-stone-200 hover:border-stone-300'}`} data-id="element-2517" data-mp="98wom0fgank5">
                    <button type="button" onClick={() => handleLoadTicket(t)} className="flex-1 text-left min-w-0" data-id="element-2518" data-mp="23ovbv9kfyxgo">
                      <div className="flex items-center gap-2" data-id="element-2519" data-mp="1tysb2n72wf7s">
                        <span className="font-bold text-stone-900" data-id="element-2520" data-mp="1tx7dsptdqi68">
                          #{t.ticketNumber || '—'}
                        </span>
                        {t.id === currentId && <span className="text-[10px] font-bold uppercase tracking-wider text-[#6a867b]" data-id="element-2521" data-mp="1jyromsdwxizd">
                            Open
                          </span>}
                      </div>
                      <div className="text-sm text-stone-700 truncate" data-id="element-2522" data-mp="30f4b6ttgo760">
                        {t.customer || t.billToName || t.jobName || 'Untitled ticket'}
                      </div>
                      <div className="text-xs text-stone-400 mt-0.5" data-id="element-2523" data-mp="tr53oe7hhk76">
                        {t.date} · saved{' '}
                        {new Date(t.updatedAt).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric'
                })}{' '}
                        {new Date(t.updatedAt).toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit'
                })}
                      </div>
                    </button>
                    <button type="button" onClick={() => handleDeleteTicket(t.id)} className="p-2 rounded-lg text-stone-300 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0" aria-label={`Delete ticket ${t.ticketNumber}`} data-id="element-2524" data-mp="1mfsuepb051s4">
                      <Trash2 className="w-4 h-4" data-id="element-2525" data-mp="3j9vzr5vv9hmm" />
                    </button>
                  </div>)}
            </div>

            <div className="p-4 border-t border-stone-200 space-y-2" data-id="element-2526" data-mp="32cykdfxsw2gm">
              <button type="button" onClick={handleNewTicket} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#8aa69b] text-white rounded-lg text-sm font-bold hover:bg-[#7a968b] transition-colors" data-id="element-2527" data-mp="374gpmg6uwp7e">
                <FilePlus2 className="w-4 h-4" data-id="element-2528" data-mp="2ei4ad5eojedz" /> Start New Ticket
              </button>
              <div className="flex gap-2" data-id="element-2529" data-mp="2jgyr81t36svg">
                <button type="button" onClick={handleBackupAll} disabled={isBackingUp || savedTickets.length === 0} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm font-bold text-stone-700 hover:bg-stone-50 transition-colors disabled:opacity-40" data-id="element-2530" data-mp="2odphfhylv4ul">
                  <Archive className="w-4 h-4" data-id="element-2531" data-mp="1s7m6aonez44x" />
                  {isBackingUp ? 'Backing up…' : 'Backup all'}
                </button>
                <button type="button" onClick={() => restoreInputRef.current?.click()} disabled={isRestoring} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm font-bold text-stone-700 hover:bg-stone-50 transition-colors disabled:opacity-40" data-id="element-2532" data-mp="1o6kbck0j5e8r">
                  <Upload className="w-4 h-4" data-id="element-2533" data-mp="14ggqg9hh4bum" />
                  {isRestoring ? 'Restoring…' : 'Restore'}
                </button>
                <input ref={restoreInputRef} type="file" accept="application/json,.json" onChange={handleRestoreFile} className="hidden" data-id="element-2534" data-mp="vhd3guf6rcau" />
              </div>
              <p className="text-[11px] text-stone-400 text-center" data-id="element-2535" data-mp="2pvw4esk2x7qr">
                Backup downloads every ticket as a JSON file. Restore re-imports
                one back into the cloud.
              </p>
            </div>
          </div>
        </div>}

      {/* Printable Form Container */}
      <div id="daily-ticket-content" className={`relative max-w-4xl mx-auto bg-white shadow-xl print:shadow-none overflow-hidden print:max-w-[8in] print:mx-auto ${isDownloading ? 'exporting' : ''}`} data-id="element-2536" data-mp="3owkbnpzzozui">
        {/* Accent bar across the very top of the sheet — a distinctive marker
            the invoice doesn't have, so the ticket reads as its own document. */}
        <div className="h-2 w-full bg-[#00899B]" aria-hidden="true" data-id="element-2537" data-mp="368f8ajn8vgw8" />
        <div className="p-10 sm:p-14 print:p-10" data-id="element-2538" data-mp="317hraadz0swb">
          {/* Header — flipped vs. the invoice: bold title + ticket chip on the
           LEFT, brand + contact block on the RIGHT. */}
          <div className="ticket-header export-row flex flex-col justify-between items-start gap-8 mb-12" data-id="element-2539" data-mp="gi1wz5fjmm1x">
            {/* Title + ticket meta (LEFT) */}
            <div className="ticket-title flex flex-col" data-id="element-2540" data-mp="p0ndlvg35akx">
              <h1 className="text-5xl sm:text-6xl tracking-tight leading-[0.95] font-[Montserrat,_sans-serif] text-[#D2D2D2] font-[100]" data-id="element-2541" data-mp="t179kvoxa85h">
                Daily Ticket
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-5" data-id="element-2542" data-mp="jyy0895e4kto">
                <div className="inline-flex items-baseline gap-1 text-black rounded-md px-3 py-1.5 bg-[#ffffff]" data-id="element-2543" data-mp="17p232ear8gjm">
                  <span className="text-sm font-bold opacity-80" data-id="element-2544" data-mp="1cnvhc8xv8bku">#</span>
                  <input type="text" value={ticketNumber} onChange={e => {
                  ticketOverridden.current = true;
                  setTicketNumber(e.target.value);
                }} className="bg-transparent border-none p-0 text-2xl font-extrabold text-black placeholder:text-stone-400 focus:ring-0 w-20 leading-none" aria-label="Ticket number" data-id="element-2545" data-mp="1cpy5sojddwng" />
                </div>
                <div className="inline-flex items-center gap-2 text-sm text-stone-600" data-id="element-2546" data-mp="1vewf4tn9212t">
                  <span className="font-bold uppercase tracking-wider text-[11px] text-stone-400 font-[Montserrat,_sans-serif]" data-id="element-2547" data-mp="25b4yv4q7kgjo">
                    Date
                  </span>
                  <input type="date" value={date} onChange={e => setDate(e.target.value)} className="bg-transparent border-none p-0 text-sm font-bold text-stone-800 focus:ring-0 w-32" data-id="element-2548" data-mp="1vcff16er4m0s" />
                </div>
              </div>
            </div>

            {/* Brand + contact block (RIGHT) */}
            <div className="ticket-brand flex flex-col text-right" data-id="element-2549" data-mp="1hj7mnciujmi1">
              <div className="font-extrabold tracking-tight text-stone-900 whitespace-nowrap text-[36px]" data-id="element-2550" data-mp="1cmh66o34qow3">
                Black<span className="text-stone-600" data-id="element-2551" data-mp="17krv58fwqb80">Rock</span>
              </div>
              <div className="font-bold uppercase tracking-[0.22em] text-stone-500 mt-1.5 mb-4 whitespace-nowrap text-[10px]" data-id="element-2552" data-mp="13kq3zt7st06z">
                Groundworks, LLC
              </div>
              <div className="text-sm text-stone-700 leading-relaxed" data-id="element-2553" data-mp="3a8govp7up3hf">
                12802 219th Avenue East
                <br data-id="element-2554" data-mp="3f7a1zgj86sh2" />
                Bonney Lake, WA 98391
                <br data-id="element-2555" data-mp="3jag7un2fehn0" />
                206-619-3742
                <br data-id="element-2556" data-mp="3japxuanh0osp" />
                dirt@blackrockgw.com
              </div>
            </div>
          </div>

          {/* Project Information + Bill To — green header band spanning both
           columns (Project info left, Bill to right), rows separated by
           subtle horizontal borders like the materials table. */}
          <div className="mb-12 border border-stone-300 rounded-md overflow-hidden" data-id="element-2557" data-mp="2vi9dtuk6amqr">
            {/* Green header band */}
            <div className="ticket-2col export-2col grid grid-cols-1 print:grid-cols-2" data-id="element-2558" data-mp="35ayq2rnjs4n5">
              <div className="ticket-col-divider flex items-center px-4 py-3.5 text-[14px] font-[500] text-[#1C1917] bg-[#CEE5E7] font-[Montserrat,_sans-serif]" data-id="element-2559" data-mp="27vb197z6byy4">
                Project info
              </div>
              <div className="flex items-center text-[#1C1917] px-4 py-3.5 text-[14px] font-[500] bg-[#CEE5E7] font-[Montserrat,_sans-serif]" data-id="element-2560" data-mp="javtww6024h6">
                Bill to
              </div>
            </div>
            {/* Two columns of labeled rows */}
            <div className="ticket-2col export-2col grid grid-cols-1 print:grid-cols-2" data-id="element-2561" data-mp="s50h95lxcxry">
              {/* Project Information column (LEFT) */}
              <div className="ticket-col-divider" data-id="element-2562" data-mp="121dgsk45i5um">
                <div className="border-b border-stone-200 px-4 py-2" data-id="element-2563" data-mp="1202600cafxu0">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 font-[Montserrat,_sans-serif]" data-id="element-2564" data-mp="x20kl8re3mfd">
                    Customer
                  </div>
                  <MinimalInput value={customer} onChange={e => handleCustomer(e.target.value)} placeholder="Customer / Contractor" className="font-bold" icon={<User className="w-4 h-4" data-id="element-2566" data-mp="38u5uo2v0utsp" />} data-id="element-2565" data-mp="170q3n4v0vcx7" />
                </div>
                <div className="border-b border-stone-200 px-4 py-2" data-id="element-2567" data-mp="2yoxvoy54t7fi">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 font-[Montserrat,_sans-serif]" data-id="element-2568" data-mp="32q9g8p95f2gu">
                    Job Name / Address
                  </div>
                  <MinimalInput value={jobName} onChange={e => setJobName(e.target.value)} placeholder="Job Name / Address" icon={<MapPin className="w-4 h-4" data-id="element-2570" data-mp="2k01g8g0qws4v" />} data-id="element-2569" data-mp="2twwc0r5mwefg" />
                </div>
                <div className="border-b border-stone-200 px-4 py-2" data-id="element-2571" data-mp="2600eyzgkjiov">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 font-[Montserrat,_sans-serif]" data-id="element-2572" data-mp="261tjo97mfsu8">
                    Phone / Email
                  </div>
                  <MinimalInput value={projectPhoneEmail} onChange={e => setProjectPhoneEmail(e.target.value)} placeholder="Phone / Email" icon={<Phone className="w-4 h-4" data-id="element-2574" data-mp="6qpe0b8fsmed" />} data-id="element-2573" data-mp="495e3t7i8tba" />
                </div>
              </div>

              {/* Bill To column (RIGHT) */}
              <div className="ticket-billto-col" data-id="element-2575" data-mp="3tykojti5pwnj">
                <div className="border-b border-stone-200 px-4 py-2" data-id="element-2576" data-mp="3tzckj5fo1wec">
                  <div className="uppercase tracking-wider text-stone-500 text-[10px] font-[700] font-[Montserrat,_sans-serif]" data-id="element-2577" data-mp="3ozzopv6thezm">
                    Name
                  </div>
                  <MinimalInput value={billToName} onChange={e => handleBillToName(e.target.value)} placeholder="Customer Name" className="font-bold" icon={<User className="w-4 h-4" data-id="element-2579" data-mp="bpsk7z05kynx" />} data-id="element-2578" data-mp="lir79cg04h5f" />
                </div>
                <div className="border-b border-stone-200 px-4 py-2" data-id="element-2580" data-mp="198lixqol3zo3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 font-[Montserrat,_sans-serif]" data-id="element-2581" data-mp="14fiyc81sc0k1">
                    Email
                  </div>
                  <MinimalInput value={email} onChange={e => setEmail(e.target.value)} placeholder="Email Address" type="email" icon={<Mail className="w-4 h-4" data-id="element-2583" data-mp="1qq0t1y75uygf" />} data-id="element-2582" data-mp="1gq9zpzuc6phx" />
                </div>
                <div className="border-b border-stone-200 px-4 py-2" data-id="element-2584" data-mp="1urcnqi4w69vh">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 font-[Montserrat,_sans-serif]" data-id="element-2585" data-mp="1zpnv8m7ps8aj">
                    Phone
                  </div>
                  <MinimalInput value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone Number" type="tel" icon={<Phone className="w-4 h-4" data-id="element-2587" data-mp="2ecqwn3z5r135" />} data-id="element-2586" data-mp="24ih15dex5mo7" />
                </div>
              </div>
            </div>
          </div>

          {/* Material / Loads Table */}
          <div className="mb-8" data-id="element-2588" data-mp="33t6uv1gpvddp">
            <div className="border border-stone-300 rounded-md overflow-hidden" data-id="element-2589" data-mp="2zpsajylqwaug">
              <div className="grid grid-cols-[40px_1fr_2fr_80px_40px] items-center text-[#1C1917] text-sm py-3.5 px-4 bg-[#CEE5E7]" data-id="element-2590" data-mp="3dp9uo0o3izxh">
                <div data-id="element-2591" data-mp="38pxa5mkp64y2">#</div>
                <div className="font-[500] font-[Montserrat,_sans-serif]" data-id="element-2592" data-mp="38qq5lre7h80w">
                  Material
                </div>
                <div className="font-[500] font-[Montserrat,_sans-serif]" data-id="element-2593" data-mp="2kyjcr27ovj68">
                  Job Description / Pick up - Delivery
                </div>
                <div className="text-center font-[500] font-[Montserrat,_sans-serif]" data-id="element-2594" data-mp="2urh2ntf0gaev">
                  Loads
                </div>
                <div className="pdf-hide print:hidden" data-id="element-2595" data-mp="2pye7xhyhyux3"></div>
              </div>

              {materials.map((row, index) => <div key={row.id} className="grid grid-cols-[40px_1fr_2fr_80px_40px] items-center border-b border-stone-200 px-4 py-2" data-id="element-2596" data-mp="1xb1jf28s0d15" data-mpi={index}>
                  <div className="text-[#00899B]" data-id="element-2597" data-mp="cr69lv437o6v">
                    <Truck className="w-4 h-4" data-id="element-2598" data-mp="hkhgtxcta6zb" />
                  </div>
                  <div className="pr-4" data-id="element-2599" data-mp="3suhtprhikcu">
                    <MinimalInput value={row.material} onChange={e => handleUpdateRow(row.id, 'material', e.target.value)} placeholder="Material" data-id="element-2600" data-mp="3tvwe47plozb" />
                  </div>
                  <div className="pr-4" data-id="element-2601" data-mp="10hsdda726mb2">
                    <MinimalInput value={row.description} onChange={e => handleUpdateRow(row.id, 'description', e.target.value)} placeholder="Description" icon={descriptionIcon(row.description)} data-id="element-2602" data-mp="mj2f3r8i6gl4" />
                  </div>
                  <div data-id="element-2603" data-mp="3c5rrfqrzpwtb">
                    <input type="number" min="0" step="1" value={row.loads || ''} onChange={e => handleUpdateRow(row.id, 'loads', parseFloat(e.target.value))} className="w-full bg-transparent border-b border-transparent hover:border-stone-200 focus:border-stone-300 focus:ring-0 px-0 py-1 text-sm text-center text-stone-800 transition-colors" data-id="element-2604" data-mp="3mgytu6om9nez" />
                  </div>
                  <button onClick={() => handleRemoveRow(row.id)} className="pdf-hide flex items-center justify-end text-stone-300 hover:text-red-500 transition-colors print:hidden" aria-label="Remove row" data-id="element-2605" data-mp="2tr003xzh923z">
                    <Trash2 className="w-4 h-4" data-id="element-2606" data-mp="2ovudsy25xlfd" />
                  </button>
                </div>)}
            </div>

            {/* Total Loads — beyond the table, free-flowing underneath it,
             still aligned to the Loads column so the total sits in the same
             vertical band as the load boxes above and Total Hours below. */}
            <div className="grid grid-cols-[40px_1fr_2fr_80px_40px] items-center px-4 py-2 mt-2" data-id="element-2607" data-mp="2a5zpzj73waeb">
              <div data-id="element-2608" data-mp="2p3a2mvvhjmbm" />
              <div data-id="element-2609" data-mp="2p2qqjvsins5i" />
              <div className="font-bold text-stone-600 text-right pr-4 text-[16px] font-[Inter,_sans-serif]" data-id="element-2610" data-mp="2p1yxosqb08ix">
                Total Loads
              </div>
              <div className="font-bold text-stone-900 text-center text-[16px]" data-id="element-2611" data-mp="2xvbyegum0efk">
                {totalLoads}
              </div>
              <div className="pdf-hide print:hidden" data-id="element-2612" data-mp="2tx4u4lhc15l9" />
            </div>

            <div className="pt-2 px-4" data-id="element-2613" data-mp="37vl18dj3wy3x">
              <button onClick={handleAddRow} className="pdf-hide text-xs font-medium text-[#8aa69b] flex items-center gap-1 hover:text-[#6a867b] transition-colors print:hidden" data-id="element-2614" data-mp="37sglciwa4dxf">
                <Plus className="w-3 h-3" data-id="element-2615" data-mp="24jdyw94uyob1" /> Add Row
              </button>
            </div>
          </div>

          {/* Totals — Time In / Out / Total Hours. No table/borders: free-flowing
           rows that reuse the SAME [40px_1fr_2fr_80px_40px] grid as the
           materials table so each value lines up in one straight vertical band
           directly beneath Total Loads. Enlarged for readability. */}
          <div className="mb-12" data-id="element-2616" data-mp="2o73ezj3fn04f">
            <div className="grid grid-cols-[40px_1fr_2fr_150px_16px] items-center px-4 py-1.5" data-id="element-2617" data-mp="2o61vcq8cp5b3">
              <div data-id="element-2618" data-mp="2sa9jomawcj8s" />
              <div data-id="element-2619" data-mp="2s7nf8vjayjus" />
              <div className="text-stone-600 text-right pr-4 text-[16px] font-[Inter,_sans-serif]" data-id="element-2620" data-mp="2x8b2r78boik9">
                Time In
              </div>
              <TimeField value={timeIn} onChange={setTimeIn} ariaLabel="Time in" data-id="element-2621" data-mp="xx06okj56fls" />
              <div className="pdf-hide print:hidden" data-id="element-2622" data-mp="vb0yt5bauj0x" />
            </div>

            <div className="grid grid-cols-[40px_1fr_2fr_150px_16px] items-center px-4 py-1.5" data-id="element-2623" data-mp="vd3v2731p8e3">
              <div data-id="element-2624" data-mp="qi7qy8kjaukm" />
              <div data-id="element-2625" data-mp="ll7l9yrljtu1" />
              <div className="text-stone-600 text-right pr-4 text-[16px] font-[Inter,_sans-serif]" data-id="element-2626" data-mp="lkoffgf80vwd">
                Time Out
              </div>
              <TimeField value={timeOut} onChange={setTimeOut} ariaLabel="Time out" data-id="element-2627" data-mp="gnxyyrzi7yaf" />
              <div className="pdf-hide print:hidden" data-id="element-2628" data-mp="19b1ywijqpmfc" />
            </div>

            <div className="grid grid-cols-[40px_1fr_2fr_150px_16px] items-center px-4 py-1.5" data-id="element-2629" data-mp="198z8vyil7tam">
              <div data-id="element-2630" data-mp="14djxdd81y9hd" />
              <div data-id="element-2631" data-mp="10adsoa0l8qd0" />
              <div className="text-right pr-4 text-stone-900 text-[20px] font-[600]" data-id="element-2632" data-mp="10avkmug7wif4">
                Total Hours
              </div>
              <div className="text-center text-stone-900 text-[20px] font-[600]" data-id="element-2633" data-mp="3p39anlt3xn7y">
                {totalHours || '-'}
              </div>
              <div className="pdf-hide print:hidden" data-id="element-2634" data-mp="1crmvml3p6dvt" />
            </div>
          </div>

          {/* Notes */}
          <div className="mb-12" data-id="element-2635" data-mp="1hiv8xs85eje9">
            <div className="text-sm text-stone-500 mb-2 font-[Montserrat,_sans-serif]" data-id="element-2636" data-mp="1hj4wl95q0egs">
              Notes
            </div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full border-b border-transparent hover:border-stone-200 focus:border-stone-300 focus:ring-0 px-0 py-1 text-sm text-stone-800 transition-colors resize-none bg-[#CEE5E72E] pl-[4px] pr-[4px]" placeholder="Thanks for your business." data-id="element-2637" data-mp="12rdzf79pgsgf" />
          </div>

          {/* Signatures */}
          <div className="ticket-sign export-2col grid grid-cols-1 gap-12 mt-12 pt-8 border-t border-stone-200" data-id="element-2638" data-mp="1qi072xfssapw">
            <SignaturePad label="Driver Signature" value={driverSignature} onChange={setDriverSignature} data-id="element-2639" data-mp="b0pxvu44dl28" />
            <SignaturePad label="Company Rep. Signature" value={companyRepSignature} onChange={setCompanyRepSignature} data-id="element-2640" data-mp="7ju4rj7zl2o7" />
          </div>
        </div>
      </div>

      {/* Page styles */}
      <style dangerouslySetInnerHTML={{
      __html: `
        /* Force this page to always render as a WHITE document, even when the
           site is in dark mode. The .dark class lives on <html>, so we override
           the dark: variants inside this page's container back to light values. */
        html.dark #daily-ticket-page { background-color: #f5f5f4; }
        html.dark #daily-ticket-page .text-white { color: #1c1917 !important; }

        /* Ticket-scoped responsive layout. The GLOBAL Tailwind sm/md/lg
           breakpoints are all pinned to 1300px in tailwind.config.js, so the
           form's sm:/md: utilities never fire on real phones or tablets — that
           left the ticket permanently stacked in "mobile" mode on every device
           below 1300px. These self-contained media queries give the ticket its
           OWN breakpoint at 640px so it looks the same (side-by-side header,
           two-column project/billing body, two signature pads) on phone,
           tablet, and desktop alike, without touching the site-wide config. */
        @media (min-width: 640px) {
          /* Header banner: title block left, brand + contact block right
             (flipped from the invoice layout so the two read differently). */
          #daily-ticket-page .ticket-header { flex-direction: row; align-items: flex-start; }
          #daily-ticket-page .ticket-title { text-align: left; }
          #daily-ticket-page .ticket-brand { text-align: right; }

          /* Project Information + Bill To: two columns */
          #daily-ticket-page .ticket-2col { grid-template-columns: 1fr 1fr; }
          /* Subtle vertical divider between the Project info and Bill to
             columns — only in the side-by-side layout. */
          #daily-ticket-page .ticket-col-divider { border-right: 1px solid #e7e5e4; }

          /* Signatures: two pads side by side */
          #daily-ticket-page .ticket-sign { grid-template-columns: 1fr 1fr; gap: 3rem; }
        }

        /* During PDF capture the content root gets the .exporting class. This
           hides interactive-only controls (trash / add-row / clear / hints) and
           forces the desktop layout so the export never stacks or shows chrome,
           regardless of the viewport the user triggered it from. */
        #daily-ticket-content.exporting { width: 896px !important; max-width: 896px !important; }
        #daily-ticket-content.exporting .pdf-hide { display: none !important; }
        #daily-ticket-content.exporting .export-row { flex-direction: row !important; }
        #daily-ticket-content.exporting .export-2col { grid-template-columns: 1fr 1fr !important; }

        /* Compact treatment applied to the CLONED node during PDF export so the
           whole ticket collapses onto a single letter page. */
        .pdf-export .mb-12 { margin-bottom: 1.5rem !important; }
        .pdf-export .mb-8 { margin-bottom: 1rem !important; }
        .pdf-export .p-10 { padding: 1.5rem !important; }
        .pdf-export .pt-8 { padding-top: 1rem !important; }
        .pdf-export .mt-12 { margin-top: 1.5rem !important; }

        @media print {
          @page { size: letter portrait; margin: 0.35in; }
          html, body { background: #fff !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          #daily-ticket-page, #daily-ticket-page * { page-break-inside: avoid; break-inside: avoid; }
          #daily-ticket-page .mb-12 { margin-bottom: 1.5rem !important; }
          #daily-ticket-page .mb-8 { margin-bottom: 1rem !important; }
          #daily-ticket-page .p-10 { padding: 1.5rem !important; }
          #daily-ticket-page .pt-8 { padding-top: 1rem !important; }
          #daily-ticket-page .mt-12 { margin-top: 1.5rem !important; }
          #daily-ticket-page textarea { min-height: 0 !important; }
        }
      `
    }} data-id="element-2641" data-mp="hi1992l39s9b" />
    </div>;
}